import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Check, Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PaymentDetailsForm } from "../components/PaymentDetailsForm";
import { useScrollIntoViewWhenReady } from "../hooks/useScrollIntoViewWhenReady";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";
import SEOHead from "../components/SEOHead";
import { recaptchaHeaders } from "../lib/recaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PackagePurchasePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [pkg, setPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const formSectionRef = useRef(null);
  useScrollToTopWhenLoading(loading);
  useScrollIntoViewWhenReady(formSectionRef, !loading && !!pkg, { mobileOnly: true });

  useEffect(() => {
    fetchPackage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPackage = async () => {
    try {
      const res = await axios.get(`${API}/packages/${slug}`);
      setPackage(res.data);
    } catch {
      toast.error("Package not found");
      navigate("/packages");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    setSubmitting(true);
    try {
      // 1. Create package purchase record
      const rcPurchaseHeaders = await recaptchaHeaders("purchase_package");
      await axios.post(`${API}/packages/purchase`, {
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        package_id: pkg.id,
      }, { headers: rcPurchaseHeaders });

      // 2. Create booking for payment tracking
      const rcBookingHeaders = await recaptchaHeaders("submit_booking");
      const bookingRes = await axios.post(`${API}/bookings`, {
        guest_first_name: formData.guest_first_name,
        guest_last_name: formData.guest_last_name,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        booking_type: "package",
        package_id: pkg.id,
        total_amount: pkg.price,
        source: "direct",
      }, { headers: rcBookingHeaders });

      const booking = bookingRes.data;

      // 3. Initialise STK push
      const paymentRes = await axios.post(`${API}/payments/initialize`, {
        booking_id: booking.id,
        amount: pkg.price,
        phone_number: formData.guest_phone,
      });

      if (!paymentRes.data.status) throw new Error("Payment initialization failed");

      const reference = paymentRes.data.reference;

      if (paymentRes.data.mock) {
        await axios.get(`${API}/payments/verify/${reference}`);
        navigate(`/payment-success?booking=${booking.booking_number}&reference=${reference}`);
        return;
      }

      // 4. Navigate to processing page
      navigate("/payment-processing", {
        state: {
          bookingNumber: booking.booking_number,
          bookingId: booking.id,
          reference,
          phone: formData.guest_phone,
          amount: pkg.price,
          customerName: formData.guest_name || `${formData.guest_first_name || ""} ${formData.guest_last_name || ""}`.trim(),
          email: formData.guest_email,
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process purchase");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-empire flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
      </div>
    );
  }

  if (!pkg) return null;

  const features = [
    pkg.credits === 999 ? "Unlimited classes" : `${pkg.credits} class credits`,
    `Valid for ${pkg.validity_days} days`,
    "Use for any class",
    "No expiry on unused days",
  ];

  const seoDescription =
    (pkg.description || "").trim().slice(0, 155) ||
    `${pkg.name} from Empire Dance Kenya — dance class credits valid ${pkg.validity_days} days. Buy online and pay with M-Pesa.`;

  const packageJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: pkg.name,
    description: pkg.description || seoDescription,
    brand: { "@type": "Organization", name: "Empire Dance Kenya" },
    offers: {
      "@type": "Offer",
      price: pkg.price,
      priceCurrency: "KES",
      availability: "https://schema.org/InStock",
      url: `https://bookings.empiredancekenya.com/packages/${pkg.slug || slug}`,
    },
  };

  return (
    <div className="container-empire max-w-4xl" data-testid="package-purchase-page">
      <SEOHead
        page="packages"
        title={`${pkg.name} | Empire Dance Kenya`}
        description={seoDescription}
        path={`/packages/${pkg.slug || slug}`}
        jsonLd={packageJsonLd}
      />
      <Button
        variant="ghost"
        onClick={() => navigate("/packages")}
        className="mb-6 text-neutral-400 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Packages
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-stretch">
        {/* Package Summary */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="h-full"
        >
          <Card className="card-empire-featured h-full flex flex-col">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-empire-gold/20 flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-empire-gold" />
              </div>
              <CardTitle className="text-2xl text-white">{pkg.name}</CardTitle>
              {pkg.description && (
                <p className="text-neutral-400">{pkg.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center py-4 border-y border-neutral-800">
                {pkg.original_price && pkg.original_price > pkg.price && (
                  <span className="text-neutral-500 line-through text-lg mr-2">
                    KES {pkg.original_price.toLocaleString()}
                  </span>
                )}
                <span className="text-4xl font-bold text-empire-gold">
                  KES {pkg.price.toLocaleString()}
                </span>
              </div>

              <ul className="space-y-3">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-neutral-300">
                    <div className="w-6 h-6 rounded-full bg-empire-gold/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-empire-gold" />
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>

        {/* Payment Details Form */}
        <motion.div
          ref={formSectionRef}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="h-full scroll-mt-24 md:scroll-mt-0"
          data-testid="package-purchase-form-section"
        >
          <Card className="card-empire h-full flex flex-col">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-empire-gold" />
                Your Details
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              <PaymentDetailsForm
                onSubmit={handleFormSubmit}
                submitting={submitting}
                amount={pkg.price}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
